import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel,
  ConnectionLineType,
  SmoothStepEdge,
  BezierEdge,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Play, Pause, GitBranch, Users, Clock, CheckCircle, XCircle,
  Settings, FileText, Zap, LayoutTemplate, ArrowRight, Circle,
  AlertTriangle, Mail, Target, Timer, Save, Trash2, Eye, RefreshCw,
  ChevronRight, List, Activity, Edit3
} from 'lucide-react';

// Custom Node Components with click-to-edit functionality
const StartNode = ({ data }) => (
  <div className="px-4 py-3 shadow-lg rounded-full bg-gradient-to-r from-emerald-600 to-emerald-500 border-2 border-emerald-400 cursor-default">
    <Handle type="target" position={Position.Left} className="opacity-0" />
    <div className="flex items-center gap-2">
      <Play className="w-4 h-4 text-white" />
      <span className="text-white font-medium text-sm">{data.label}</span>
    </div>
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-emerald-300 !border-2 !border-white" />
  </div>
);

const TaskNode = ({ data }) => (
  <div 
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-sky-600 to-sky-500 border-2 border-sky-400 min-w-[160px] max-w-[200px] cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
    onClick={() => data.onEdit && data.onEdit(data.stage)}
  >
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-sky-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <FileText className="w-4 h-4 text-white flex-shrink-0" />
      <span className="text-white font-medium text-sm truncate">{data.label}</span>
      <Edit3 className="w-3 h-3 text-sky-200 ml-auto opacity-60" />
    </div>
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      {data.sla_hours && (
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3 text-sky-200" />
          <span className="text-sky-100 text-xs">{data.sla_hours}h</span>
        </div>
      )}
      {data.assignee_id && (
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3 text-sky-200" />
          <span className="text-sky-100 text-xs capitalize">{data.assignee_id.replace('_', ' ')}</span>
        </div>
      )}
    </div>
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-sky-300 !border-2 !border-white" />
  </div>
);

const ApprovalNode = ({ data }) => (
  <div 
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-amber-600 to-amber-500 border-2 border-amber-400 min-w-[160px] max-w-[200px] cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
    onClick={() => data.onEdit && data.onEdit(data.stage)}
  >
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-amber-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <CheckCircle className="w-4 h-4 text-white flex-shrink-0" />
      <span className="text-white font-medium text-sm truncate">{data.label}</span>
      <Edit3 className="w-3 h-3 text-amber-200 ml-auto opacity-60" />
    </div>
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      {data.approval_type && (
        <Badge className="text-xs bg-amber-800/50 text-amber-100 capitalize">{data.approval_type}</Badge>
      )}
      {data.sla_hours && (
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3 text-amber-200" />
          <span className="text-amber-100 text-xs">{data.sla_hours}h</span>
        </div>
      )}
    </div>
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-amber-300 !border-2 !border-white" />
  </div>
);

const DecisionNode = ({ data }) => (
  <div 
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-purple-600 to-purple-500 border-2 border-purple-400 cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
    onClick={() => data.onEdit && data.onEdit(data.stage)}
  >
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-purple-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <GitBranch className="w-4 h-4 text-white" />
      <span className="text-white font-medium text-sm">{data.label}</span>
      <Edit3 className="w-3 h-3 text-purple-200 ml-auto opacity-60" />
    </div>
    {data.conditions?.length > 0 && (
      <div className="text-purple-200 text-xs mt-1">
        {data.conditions.length} condition{data.conditions.length > 1 ? 's' : ''}
      </div>
    )}
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-purple-300 !border-2 !border-white" />
    <Handle type="source" position={Position.Top} id="top" className="!w-3 !h-3 !bg-purple-300 !border-2 !border-white" />
    <Handle type="source" position={Position.Bottom} id="bottom" className="!w-3 !h-3 !bg-purple-300 !border-2 !border-white" />
  </div>
);

const EndNode = ({ data }) => (
  <div className="px-4 py-3 shadow-lg rounded-full bg-gradient-to-r from-red-600 to-red-500 border-2 border-red-400 cursor-default">
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-red-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <Circle className="w-4 h-4 text-white fill-white" />
      <span className="text-white font-medium text-sm">{data.label}</span>
    </div>
    <Handle type="source" position={Position.Right} className="opacity-0" />
  </div>
);

const NotificationNode = ({ data }) => (
  <div 
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-pink-600 to-pink-500 border-2 border-pink-400 min-w-[140px] cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
    onClick={() => data.onEdit && data.onEdit(data.stage)}
  >
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-pink-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <Mail className="w-4 h-4 text-white" />
      <span className="text-white font-medium text-sm">{data.label}</span>
      <Edit3 className="w-3 h-3 text-pink-200 ml-auto opacity-60" />
    </div>
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-pink-300 !border-2 !border-white" />
  </div>
);

const TimerNode = ({ data }) => (
  <div 
    className="px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-slate-600 to-slate-500 border-2 border-slate-400 min-w-[140px] cursor-pointer hover:ring-2 hover:ring-white/50 transition-all"
    onClick={() => data.onEdit && data.onEdit(data.stage)}
  >
    <Handle type="target" position={Position.Left} className="!w-3 !h-3 !bg-slate-300 !border-2 !border-white" />
    <div className="flex items-center gap-2">
      <Timer className="w-4 h-4 text-white" />
      <span className="text-white font-medium text-sm">{data.label}</span>
      <Edit3 className="w-3 h-3 text-slate-200 ml-auto opacity-60" />
    </div>
    <Handle type="source" position={Position.Right} className="!w-3 !h-3 !bg-slate-300 !border-2 !border-white" />
  </div>
);

const nodeTypes = {
  start: StartNode,
  task: TaskNode,
  approval: ApprovalNode,
  review: TaskNode,
  decision: DecisionNode,
  end: EndNode,
  notification: NotificationNode,
  timer: TimerNode,
  parallel: TaskNode,
  integration: TaskNode,
};

// Edge types for ReactFlow
const edgeTypes = {
  smoothstep: SmoothStepEdge,
  default: BezierEdge,
};

// Default edge options
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#64748b', strokeWidth: 2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#64748b',
  },
};

export default function WorkflowBuilderPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  
  // State
  const [templates, setTemplates] = useState([]);
  const [prebuiltTemplates, setPrebuiltTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPrebuiltDialog, setShowPrebuiltDialog] = useState(false);
  const [showAddStageDialog, setShowAddStageDialog] = useState(false);
  const [showEditStageDialog, setShowEditStageDialog] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Form states
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    category: 'safety',
    entity_type: 'incident'
  });
  
  const [stageForm, setStageForm] = useState({
    name: '',
    stage_type: 'task',
    assignee_type: 'role',
    assignee_id: 'safety_manager',
    sla_hours: 24
  });

  // Edit stage form
  const [editStageForm, setEditStageForm] = useState({
    id: '',
    name: '',
    stage_type: 'task',
    assignee_type: 'role',
    assignee_id: 'safety_manager',
    sla_hours: 24,
    next_stage_ids: []
  });

  // Fetch data
  const fetchTemplates = async () => {
    try {
      const response = await api.get('/workflows/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchPrebuiltTemplates = async () => {
    try {
      const response = await api.get('/workflows/templates/prebuilt');
      setPrebuiltTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch prebuilt templates:', error);
    }
  };

  const fetchInstances = async () => {
    try {
      const response = await api.get('/workflows/instances');
      setInstances(response.data || []);
    } catch (error) {
      console.error('Failed to fetch instances:', error);
    }
  };

  const fetchMyTasks = async () => {
    try {
      const response = await api.get('/workflows/instances/my-tasks');
      setMyTasks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch my tasks:', error);
    }
  };

  const fetchTriggers = async () => {
    try {
      const response = await api.get('/workflows/triggers');
      setTriggers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch triggers:', error);
    }
  };

  const toggleTrigger = async (templateId, triggerId, currentState) => {
    try {
      await api.put(`/workflows/templates/${templateId}/triggers/${triggerId}?is_active=${!currentState}`);
      toast.success(`Trigger ${!currentState ? 'enabled' : 'disabled'}`);
      fetchTriggers();
    } catch (error) {
      toast.error('Failed to update trigger');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/workflows/stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTemplates(),
        fetchPrebuiltTemplates(),
        fetchInstances(),
        fetchMyTasks(),
        fetchStats(),
        fetchTriggers()
      ]);
      setLoading(false);
    };
    loadData();
  }, [api]);

  // Build flow from template with proper connections and condition labels
  const buildFlowFromTemplate = useCallback((template) => {
    if (!template) return;
    
    const newNodes = [];
    const newEdges = [];
    
    // Color mapping for edge types
    const edgeColors = {
      normal: '#64748b',      // slate
      condition: '#8b5cf6',   // purple
      default: '#f59e0b',     // amber
      approval: '#22c55e',    // green
      rejection: '#ef4444'    // red
    };
    
    template.stages?.forEach((stage) => {
      newNodes.push({
        id: stage.id,
        type: stage.stage_type,
        position: { x: stage.position_x || 100, y: stage.position_y || 100 },
        data: { 
          label: stage.name,
          sla_hours: stage.sla_hours,
          approval_type: stage.approval_type,
          assignee_id: stage.assignee_id,
          description: stage.description,
          conditions: stage.conditions,
          stage: stage,  // Pass the full stage for editing
          onEdit: openEditStageDialog,  // Pass edit callback
          ...stage
        }
      });
      
      // Handle DECISION nodes with conditions
      if (stage.stage_type === 'decision' && stage.conditions?.length > 0) {
        // Create edges for each condition
        stage.conditions.forEach((condition, idx) => {
          const conditionLabel = getConditionLabel(condition);
          newEdges.push({
            id: `e-${stage.id}-${condition.next_stage_id}-c${idx}`,
            source: stage.id,
            target: condition.next_stage_id,
            type: 'smoothstep',
            animated: true,
            label: conditionLabel,
            labelStyle: { fill: '#a855f7', fontWeight: 600, fontSize: 10 },
            labelBgStyle: { fill: '#1e1b4b', fillOpacity: 0.9 },
            labelBgPadding: [4, 4],
            labelBgBorderRadius: 4,
            style: { stroke: edgeColors.condition, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: edgeColors.condition }
          });
        });
        
        // Default path for unmatched conditions
        if (stage.default_next_stage_id) {
          newEdges.push({
            id: `e-${stage.id}-${stage.default_next_stage_id}-default`,
            source: stage.id,
            target: stage.default_next_stage_id,
            type: 'smoothstep',
            animated: true,
            label: 'Default',
            labelStyle: { fill: '#f59e0b', fontWeight: 600, fontSize: 10 },
            labelBgStyle: { fill: '#451a03', fillOpacity: 0.9 },
            labelBgPadding: [4, 4],
            labelBgBorderRadius: 4,
            style: { stroke: edgeColors.default, strokeWidth: 2, strokeDasharray: '5,5' },
            markerEnd: { type: MarkerType.ArrowClosed, color: edgeColors.default }
          });
        }
      } else {
        // Regular linear flow edges
        stage.next_stage_ids?.forEach((nextId, idx) => {
          const isApproval = stage.stage_type === 'approval';
          newEdges.push({
            id: `e-${stage.id}-${nextId}`,
            source: stage.id,
            target: nextId,
            type: 'smoothstep',
            animated: true,
            label: isApproval && idx === 0 ? 'Approved' : null,
            labelStyle: isApproval ? { fill: '#22c55e', fontWeight: 600, fontSize: 10 } : undefined,
            labelBgStyle: isApproval ? { fill: '#052e16', fillOpacity: 0.9 } : undefined,
            labelBgPadding: [4, 4],
            labelBgBorderRadius: 4,
            style: { stroke: isApproval ? edgeColors.approval : edgeColors.normal, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: isApproval ? edgeColors.approval : edgeColors.normal }
          });
        });
      }
    });
    
    setNodes(newNodes);
    setEdges(newEdges);
  }, [setNodes, setEdges]);

  // Helper to create human-readable condition label
  const getConditionLabel = (condition) => {
    const operatorMap = {
      'equals': '=',
      'not_equals': '≠',
      'greater_than': '>',
      'less_than': '<',
      'contains': '∋',
      'is_empty': 'empty',
      'is_not_empty': '!empty',
      'in_list': '∈'
    };
    
    const field = condition.field?.replace(/_/g, ' ');
    const op = operatorMap[condition.operator] || condition.operator;
    let value = condition.value;
    
    // Format arrays nicely
    if (Array.isArray(value)) {
      value = value.join(', ');
    }
    
    return `${field} ${op} ${value}`;
  };

  // Open template in editor - fetch fresh data from server
  const openTemplateEditor = async (template) => {
    try {
      // Fetch the latest version from the server
      const response = await api.get(`/workflows/templates/${template.id}`);
      const freshTemplate = response.data;
      setSelectedTemplate(freshTemplate);
      buildFlowFromTemplate(freshTemplate);
      setActiveTab('editor');
    } catch (error) {
      // Fallback to passed template if fetch fails
      console.error('Failed to fetch template:', error);
      setSelectedTemplate(template);
      buildFlowFromTemplate(template);
      setActiveTab('editor');
    }
  };

  // Create from prebuilt
  const handleCreateFromPrebuilt = async (templateId) => {
    setIsSubmitting(true);
    try {
      const response = await api.post(`/workflows/templates/from-prebuilt/${templateId}`);
      toast.success('Workflow created from template');
      setShowPrebuiltDialog(false);
      await fetchTemplates();
      await fetchStats();
      openTemplateEditor(response.data);
    } catch (error) {
      toast.error('Failed to create workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create new template
  const handleCreateTemplate = async () => {
    if (!createForm.name) {
      toast.error('Please enter a workflow name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await api.post('/workflows/templates', {
        ...createForm,
        stages: [
          { id: 'start', name: 'Start', stage_type: 'start', position_x: 100, position_y: 300, next_stage_ids: [] },
          { id: 'end', name: 'End', stage_type: 'end', position_x: 500, position_y: 300, next_stage_ids: [] }
        ]
      });
      toast.success('Workflow template created');
      setShowCreateDialog(false);
      setCreateForm({ name: '', description: '', category: 'safety', entity_type: 'incident' });
      await fetchTemplates();
      await fetchStats();
      openTemplateEditor(response.data);
    } catch (error) {
      toast.error('Failed to create workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add stage to template (with auto-connect)
  const handleAddStage = async () => {
    if (!selectedTemplate || !stageForm.name) {
      toast.error('Please enter a stage name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const existingStages = selectedTemplate.stages || [];
      const newStageId = `stage-${Date.now()}`;
      
      // Find the stage that currently points to 'end' (or start if no intermediate stages)
      const endStage = existingStages.find(s => s.id === 'end' || s.stage_type === 'end');
      const startStage = existingStages.find(s => s.id === 'start' || s.stage_type === 'start');
      
      // Find the stage that points to end (the "last" stage before end)
      let predecessorStage = existingStages.find(s => 
        s.next_stage_ids?.includes('end') || s.next_stage_ids?.includes(endStage?.id)
      );
      
      // If no predecessor found, use start
      if (!predecessorStage) {
        predecessorStage = startStage;
      }
      
      // Calculate position (between predecessor and end)
      const predecessorX = predecessorStage?.position_x || 100;
      const endX = endStage?.position_x || 700;
      const newX = predecessorX + ((endX - predecessorX) / 2);
      
      const newStage = {
        id: newStageId,
        name: stageForm.name,
        stage_type: stageForm.stage_type,
        assignee_type: stageForm.assignee_type,
        assignee_id: stageForm.assignee_id,
        sla_hours: stageForm.sla_hours,
        position_x: newX,
        position_y: predecessorStage?.position_y || 300,
        next_stage_ids: [endStage?.id || 'end']  // Point to end by default
      };
      
      // Update predecessor to point to new stage instead of end
      const updatedStages = existingStages.map(stage => {
        if (stage.id === predecessorStage?.id) {
          return {
            ...stage,
            next_stage_ids: (stage.next_stage_ids || [])
              .filter(id => id !== 'end' && id !== endStage?.id)
              .concat([newStageId])
          };
        }
        return stage;
      });
      
      // Move end stage to the right to make room
      const finalStages = updatedStages.map(stage => {
        if (stage.id === 'end' || stage.stage_type === 'end') {
          return {
            ...stage,
            position_x: Math.max(stage.position_x || 700, newX + 250)
          };
        }
        return stage;
      });
      
      // Add the new stage
      finalStages.push(newStage);
      
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { 
        stages: finalStages 
      });
      
      toast.success('Stage added and connected');
      setShowAddStageDialog(false);
      setStageForm({
        name: '',
        stage_type: 'task',
        assignee_type: 'role',
        assignee_id: 'safety_manager',
        sla_hours: 24
      });
      
      // Update the selected template and rebuild the flow
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      buildFlowFromTemplate(updatedTemplate);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to add stage:', error);
      toast.error('Failed to add stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open edit stage dialog
  const openEditStageDialog = (stage) => {
    if (stage.stage_type === 'start' || stage.stage_type === 'end') {
      toast.info('Start and End stages cannot be edited');
      return;
    }
    setEditingStage(stage);
    setEditStageForm({
      id: stage.id,
      name: stage.name,
      stage_type: stage.stage_type,
      assignee_type: stage.assignee_type || 'role',
      assignee_id: stage.assignee_id || 'safety_manager',
      sla_hours: stage.sla_hours || 24,
      next_stage_ids: stage.next_stage_ids || []
    });
    setShowEditStageDialog(true);
  };

  // Handle edit stage
  const handleEditStage = async () => {
    if (!selectedTemplate || !editStageForm.name) {
      toast.error('Please enter a stage name');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const updatedStages = selectedTemplate.stages.map(stage => {
        if (stage.id === editStageForm.id) {
          return {
            ...stage,
            name: editStageForm.name,
            stage_type: editStageForm.stage_type,
            assignee_type: editStageForm.assignee_type,
            assignee_id: editStageForm.assignee_id,
            sla_hours: editStageForm.sla_hours,
            next_stage_ids: editStageForm.next_stage_ids
          };
        }
        return stage;
      });
      
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { 
        stages: updatedStages 
      });
      
      toast.success('Stage updated successfully');
      setShowEditStageDialog(false);
      setEditingStage(null);
      
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      buildFlowFromTemplate(updatedTemplate);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast.error('Failed to update stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete stage
  const handleDeleteStage = async (stageId) => {
    if (!selectedTemplate) return;
    
    const stage = selectedTemplate.stages.find(s => s.id === stageId);
    if (stage?.stage_type === 'start' || stage?.stage_type === 'end') {
      toast.error('Cannot delete Start or End stages');
      return;
    }
    
    if (!window.confirm(`Delete stage "${stage?.name}"? This will also remove all connections to this stage.`)) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Find what this stage points to
      const nextStageIds = stage?.next_stage_ids || [];
      
      // Remove the stage and update connections
      const updatedStages = selectedTemplate.stages
        .filter(s => s.id !== stageId)
        .map(s => {
          // If this stage pointed to the deleted stage, rewire to the deleted stage's targets
          if (s.next_stage_ids?.includes(stageId)) {
            const newNextIds = s.next_stage_ids
              .filter(id => id !== stageId)
              .concat(nextStageIds);
            return { ...s, next_stage_ids: [...new Set(newNextIds)] };
          }
          return s;
        });
      
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { 
        stages: updatedStages 
      });
      
      toast.success('Stage deleted');
      setShowEditStageDialog(false);
      setEditingStage(null);
      
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      buildFlowFromTemplate(updatedTemplate);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to delete stage:', error);
      toast.error('Failed to delete stage');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle manual connection (when user draws an edge)
  const onConnectHandler = useCallback(async (params) => {
    if (!selectedTemplate) return;
    
    const { source, target } = params;
    
    // Update the source stage's next_stage_ids
    const updatedStages = selectedTemplate.stages.map(stage => {
      if (stage.id === source) {
        const nextIds = stage.next_stage_ids || [];
        if (!nextIds.includes(target)) {
          return { ...stage, next_stage_ids: [...nextIds, target] };
        }
      }
      return stage;
    });
    
    try {
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { 
        stages: updatedStages 
      });
      
      toast.success('Connection added');
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      buildFlowFromTemplate(updatedTemplate);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to add connection:', error);
      toast.error('Failed to add connection');
    }
  }, [selectedTemplate, api]);

  // Handle edge deletion
  const onEdgeDelete = useCallback(async (edgesToDelete) => {
    if (!selectedTemplate || !edgesToDelete.length) return;
    
    const edge = edgesToDelete[0];
    const [source, target] = edge.id.split('-to-');
    
    // Update the source stage's next_stage_ids
    const updatedStages = selectedTemplate.stages.map(stage => {
      if (stage.id === source) {
        return { 
          ...stage, 
          next_stage_ids: (stage.next_stage_ids || []).filter(id => id !== target)
        };
      }
      return stage;
    });
    
    try {
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { 
        stages: updatedStages 
      });
      
      toast.success('Connection removed');
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      buildFlowFromTemplate(updatedTemplate);
      await fetchTemplates();
    } catch (error) {
      console.error('Failed to remove connection:', error);
      toast.error('Failed to remove connection');
    }
  }, [selectedTemplate, api]);

  // Publish template
  const handlePublishTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      await api.post(`/workflows/templates/${selectedTemplate.id}/publish`);
      toast.success('Workflow published!');
      await fetchTemplates();
      await fetchStats();
      setSelectedTemplate({ ...selectedTemplate, status: 'active' });
    } catch (error) {
      toast.error('Failed to publish workflow');
    }
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    // Update stages with positions from nodes
    const stages = selectedTemplate.stages.map(stage => {
      const node = nodes.find(n => n.id === stage.id);
      return {
        ...stage,
        position_x: node?.position.x || stage.position_x,
        position_y: node?.position.y || stage.position_y
      };
    });
    
    try {
      const response = await api.put(`/workflows/templates/${selectedTemplate.id}`, { stages });
      toast.success('Workflow saved');
      
      // Update selectedTemplate with the saved data (including new positions)
      const updatedTemplate = response.data;
      setSelectedTemplate(updatedTemplate);
      
      // Refresh templates list
      await fetchTemplates();
    } catch (error) {
      toast.error('Failed to save workflow');
    }
  };

  // Advance workflow instance
  const handleAdvanceWorkflow = async (instanceId) => {
    try {
      await api.post(`/workflows/instances/${instanceId}/advance`, null, {
        params: { action: 'complete' }
      });
      toast.success('Task completed');
      await fetchInstances();
      await fetchMyTasks();
      await fetchStats();
    } catch (error) {
      toast.error('Failed to advance workflow');
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this workflow template?')) return;
    
    try {
      await api.delete(`/workflows/templates/${templateId}`);
      toast.success('Workflow deleted');
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
        setActiveTab('templates');
      }
      await fetchTemplates();
      await fetchStats();
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  const onConnect = useCallback((params) => {
    setEdges((eds) => addEdge({ ...params, animated: true, markerEnd: { type: MarkerType.ArrowClosed } }, eds));
  }, [setEdges]);

  const statusColors = {
    draft: 'bg-slate-600 text-slate-200',
    active: 'bg-emerald-600 text-emerald-100',
    paused: 'bg-yellow-600 text-yellow-100',
    archived: 'bg-gray-600 text-gray-200'
  };

  const categoryIcons = {
    safety: AlertTriangle,
    quality: CheckCircle,
    compliance: FileText,
    operations: Settings
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="workflow-title">
            Workflow Builder
          </h1>
          <p className="text-slate-400 mt-1">Design and automate business processes</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowPrebuiltDialog(true)} className="border-slate-700 text-slate-300">
            <LayoutTemplate className="w-4 h-4 mr-2" />
            Pre-built Templates
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-sky-600 hover:bg-sky-700">
            <Plus className="w-4 h-4 mr-2" />
            New Workflow
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center">
                <GitBranch className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.templates?.total || 0}</p>
                <p className="text-slate-400 text-sm">Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.templates?.active || 0}</p>
                <p className="text-slate-400 text-sm">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-600/20 flex items-center justify-center">
                <Activity className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.instances?.active || 0}</p>
                <p className="text-slate-400 text-sm">Running</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.my_pending_tasks || 0}</p>
                <p className="text-slate-400 text-sm">My Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-pink-600/20 flex items-center justify-center">
                <LayoutTemplate className="w-5 h-5 text-pink-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.prebuilt_templates || 0}</p>
                <p className="text-slate-400 text-sm">Pre-built</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="templates" className="data-[state=active]:bg-slate-800">
            <GitBranch className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="editor" className="data-[state=active]:bg-slate-800" disabled={!selectedTemplate}>
            <Settings className="w-4 h-4 mr-2" />
            Visual Editor
          </TabsTrigger>
          <TabsTrigger value="triggers" className="data-[state=active]:bg-slate-800">
            <Zap className="w-4 h-4 mr-2" />
            Auto-Triggers ({triggers.filter(t => t.is_active).length})
          </TabsTrigger>
          <TabsTrigger value="instances" className="data-[state=active]:bg-slate-800">
            <Activity className="w-4 h-4 mr-2" />
            Running Workflows
          </TabsTrigger>
          <TabsTrigger value="my-tasks" className="data-[state=active]:bg-slate-800">
            <List className="w-4 h-4 mr-2" />
            My Tasks ({myTasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Templates List */}
        <TabsContent value="templates" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8" />
            </div>
          ) : templates.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <GitBranch className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <h3 className="text-lg font-medium text-white mb-2">No Workflows Yet</h3>
                <p className="text-slate-400 mb-4">Create your first workflow or use a pre-built template</p>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={() => setShowPrebuiltDialog(true)} className="border-slate-700">
                    <LayoutTemplate className="w-4 h-4 mr-2" />
                    Pre-built Templates
                  </Button>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create New
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => {
                const CategoryIcon = categoryIcons[template.category] || FileText;
                return (
                  <Card 
                    key={template.id}
                    className="bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-colors cursor-pointer"
                    onClick={() => openTemplateEditor(template)}
                    data-testid={`workflow-card-${template.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-sky-600/20 flex items-center justify-center flex-shrink-0">
                            <CategoryIcon className="w-6 h-6 text-sky-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">{template.name}</h3>
                            <p className="text-slate-400 text-sm mt-0.5">{template.description || `${template.entity_type} workflow`}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={statusColors[template.status]}>{template.status}</Badge>
                              <Badge variant="outline" className="border-slate-700 text-slate-400 capitalize">
                                {template.category}
                              </Badge>
                              <Badge variant="outline" className="border-slate-700 text-slate-400">
                                {template.entity_type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-sky-400 font-semibold">{template.stages?.length || 0}</p>
                            <p className="text-slate-500">Stages</p>
                          </div>
                          <div className="text-center">
                            <p className="text-purple-400 font-semibold">v{template.version || 1}</p>
                            <p className="text-slate-500">Version</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Visual Editor */}
        <TabsContent value="editor" className="mt-6">
          {selectedTemplate && (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="border-b border-slate-800 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">{selectedTemplate.name}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={statusColors[selectedTemplate.status]}>{selectedTemplate.status}</Badge>
                      <span className="text-slate-400 text-sm">Version {selectedTemplate.version || 1}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowAddStageDialog(true)} className="border-sky-700 text-sky-400 hover:bg-sky-900/30">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Stage
                    </Button>
                    {selectedTemplate.status === 'draft' && (
                      <Button variant="outline" onClick={handlePublishTemplate} className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30">
                        <Play className="w-4 h-4 mr-1" />
                        Publish
                      </Button>
                    )}
                    <Button onClick={handleSaveTemplate} className="bg-sky-600 hover:bg-sky-700">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div style={{ height: '600px' }} className="bg-slate-950">
                  <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnectHandler}
                    onEdgesDelete={onEdgeDelete}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    defaultEdgeOptions={defaultEdgeOptions}
                    connectionLineType={ConnectionLineType.SmoothStep}
                    connectionLineStyle={{ stroke: '#3b82f6', strokeWidth: 2 }}
                    deleteKeyCode={['Backspace', 'Delete']}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    proOptions={{ hideAttribution: true }}
                    snapToGrid={true}
                    snapGrid={[20, 20]}
                  >
                    <Controls className="bg-slate-800 border-slate-700 text-white" />
                    <Background color="#334155" gap={20} />
                    <Panel position="top-left" className="bg-slate-900/90 p-3 rounded-lg border border-slate-700">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-emerald-500" />
                            <span className="text-slate-400">Start</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-sky-500" />
                            <span className="text-slate-400">Task</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-amber-500" />
                            <span className="text-slate-400">Approval</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded bg-purple-500" />
                            <span className="text-slate-400">Decision</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div className="w-3 h-3 rounded-full bg-red-500" />
                            <span className="text-slate-400">End</span>
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 border-t border-slate-700 pt-2 mt-1">
                          <span className="text-sky-400">Click</span> stage to edit • 
                          <span className="text-sky-400"> Drag</span> from handle to connect • 
                          <span className="text-sky-400"> Delete</span> key to remove
                        </div>
                      </div>
                    </Panel>
                  </ReactFlow>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Auto-Triggers Management */}
        <TabsContent value="triggers" className="mt-6">
          <Card className="bg-slate-900/60 border-slate-800 mb-6">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                Workflow Auto-Triggers
              </CardTitle>
              <p className="text-slate-400 text-sm mt-1">
                Configure automatic workflow initiation based on entity events. When triggers are enabled, 
                workflows will start automatically when matching conditions are met.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {triggers.length === 0 ? (
                <div className="py-12 text-center">
                  <Zap className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-lg font-medium text-white mb-2">No Triggers Configured</h3>
                  <p className="text-slate-400">Create workflows from pre-built templates to see available triggers</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {triggers.map((trigger) => (
                    <div key={`${trigger.workflow_id}-${trigger.id}`} className="p-5 hover:bg-slate-800/30 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-white font-medium">{trigger.name}</span>
                            <Badge variant="outline" className={
                              trigger.is_active 
                                ? 'border-emerald-500 text-emerald-400' 
                                : 'border-slate-600 text-slate-400'
                            }>
                              {trigger.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge className="bg-sky-600/20 text-sky-400">
                              {trigger.trigger_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-slate-400 text-sm mb-3">{trigger.description}</p>
                          
                          <div className="flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <GitBranch className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-300">{trigger.workflow_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4 text-slate-500" />
                              <span className="text-slate-400 capitalize">{trigger.entity_type}</span>
                            </div>
                          </div>
                          
                          {trigger.conditions?.length > 0 && (
                            <div className="mt-3 p-3 rounded-lg bg-slate-800/50">
                              <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Conditions</p>
                              <div className="flex flex-wrap gap-2">
                                {trigger.conditions.map((cond, idx) => (
                                  <Badge key={idx} variant="outline" className="border-purple-500/50 text-purple-300">
                                    {cond.field} {cond.operator} {Array.isArray(cond.value) ? cond.value.join(', ') : cond.value}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            variant={trigger.is_active ? 'destructive' : 'default'}
                            onClick={() => toggleTrigger(trigger.workflow_id, trigger.id, trigger.is_active)}
                            disabled={trigger.workflow_status !== 'active'}
                            className={!trigger.is_active ? 'bg-emerald-600 hover:bg-emerald-500' : ''}
                          >
                            {trigger.is_active ? (
                              <>
                                <Pause className="w-4 h-4 mr-1" />
                                Disable
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                Enable
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      
                      {trigger.workflow_status !== 'active' && (
                        <div className="mt-3 flex items-center gap-2 text-amber-400 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          Workflow must be published to enable triggers
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Auto-Trigger Info Card */}
          <Card className="bg-gradient-to-r from-sky-900/30 to-purple-900/30 border-sky-800/50">
            <CardContent className="p-5">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                How Auto-Triggers Work
              </h3>
              <ul className="text-slate-300 text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong>Incident Investigation:</strong> Automatically starts when high-severity incidents (Catastrophic/Hazardous) are reported</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span><strong>CAPA Lifecycle:</strong> Automatically initiates the full CAPA process when a new CAPA is created</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>Workflows must be <strong>Published</strong> before triggers become active</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400 mt-1">•</span>
                  <span>You can enable/disable individual triggers without affecting the workflow itself</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Running Instances */}
        <TabsContent value="instances" className="mt-6">
          {instances.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <Activity className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <h3 className="text-lg font-medium text-white mb-2">No Running Workflows</h3>
                <p className="text-slate-400">Workflows will appear here when they are started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {instances.map((instance) => (
                <Card key={instance.id} className="bg-slate-900/60 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-medium">{instance.workflow_name}</h3>
                        <p className="text-slate-400 text-sm mt-1">
                          {instance.entity_type}: {instance.entity_id.slice(0, 8)}...
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={instance.status === 'active' ? 'bg-emerald-600' : 'bg-slate-600'}>
                            {instance.status}
                          </Badge>
                          <span className="text-slate-500 text-sm">
                            Current: {instance.current_stage_name}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-slate-400">
                        Started: {new Date(instance.started_at).toLocaleDateString()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* My Tasks */}
        <TabsContent value="my-tasks" className="mt-6">
          {myTasks.length === 0 ? (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-600" />
                <h3 className="text-lg font-medium text-white mb-2">All Caught Up!</h3>
                <p className="text-slate-400">You have no pending workflow tasks</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myTasks.map((task) => (
                <Card key={task.id} className="bg-slate-900/60 border-slate-800">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-medium">{task.current_stage_name}</h3>
                        <p className="text-slate-400 text-sm mt-1">{task.workflow_name}</p>
                        <p className="text-slate-500 text-xs mt-1">
                          {task.entity_type}: {task.entity_id.slice(0, 8)}...
                        </p>
                      </div>
                      <Button onClick={() => handleAdvanceWorkflow(task.id)} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Workflow Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Workflow</DialogTitle>
            <DialogDescription className="text-slate-400">
              Design a custom workflow from scratch
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Workflow Name</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="e.g., Incident Investigation"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select value={createForm.category} onValueChange={(v) => setCreateForm({ ...createForm, category: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="quality">Quality</SelectItem>
                    <SelectItem value="compliance">Compliance</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Entity Type</Label>
                <Select value={createForm.entity_type} onValueChange={(v) => setCreateForm({ ...createForm, entity_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="incident">Incident</SelectItem>
                    <SelectItem value="audit">Audit</SelectItem>
                    <SelectItem value="capa">CAPA</SelectItem>
                    <SelectItem value="risk">Risk</SelectItem>
                    <SelectItem value="change_request">Change Request</SelectItem>
                    <SelectItem value="audit_finding">Audit Finding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Describe the workflow purpose"
                className="bg-slate-800 border-slate-700 mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleCreateTemplate} disabled={isSubmitting} className="bg-sky-600 hover:bg-sky-700">
              {isSubmitting ? 'Creating...' : 'Create Workflow'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-built Templates Dialog */}
      <Dialog open={showPrebuiltDialog} onOpenChange={setShowPrebuiltDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">Pre-built Workflow Templates</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose from industry-standard SMS workflow templates
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {prebuiltTemplates.map((template) => {
              const CategoryIcon = categoryIcons[template.category] || FileText;
              return (
                <Card 
                  key={template.id}
                  className="bg-slate-800 border-slate-700 hover:border-sky-600 cursor-pointer transition-colors"
                  onClick={() => handleCreateFromPrebuilt(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center flex-shrink-0">
                        <CategoryIcon className="w-5 h-5 text-sky-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{template.name}</h4>
                        <p className="text-slate-400 text-xs mt-1 line-clamp-2">{template.description}</p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs border-slate-600 text-slate-400 capitalize">
                            {template.category}
                          </Badge>
                          <Badge variant="outline" className="text-xs border-sky-700 text-sky-400">
                            {template.stage_count} stages
                          </Badge>
                          {template.has_escalation && (
                            <Badge variant="outline" className="text-xs border-amber-700 text-amber-400">
                              Escalation
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPrebuiltDialog(false)} className="border-slate-700">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog open={showAddStageDialog} onOpenChange={setShowAddStageDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Stage</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new stage to the workflow template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Stage Name</Label>
              <Input
                value={stageForm.name}
                onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                placeholder="e.g., Initial Review"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Stage Type</Label>
                <Select value={stageForm.stage_type} onValueChange={(v) => setStageForm({ ...stageForm, stage_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="approval">Approval</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="decision">Decision</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                    <SelectItem value="timer">Timer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">SLA Hours</Label>
                <Input
                  type="number"
                  value={stageForm.sla_hours}
                  onChange={(e) => setStageForm({ ...stageForm, sla_hours: parseInt(e.target.value) || 24 })}
                  className="bg-slate-800 border-slate-700 mt-1"
                  min="1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Assignee Type</Label>
                <Select value={stageForm.assignee_type} onValueChange={(v) => setStageForm({ ...stageForm, assignee_type: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="role">Role</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Assignee</Label>
                <Select value={stageForm.assignee_id} onValueChange={(v) => setStageForm({ ...stageForm, assignee_id: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="safety_manager">Safety Manager</SelectItem>
                    <SelectItem value="quality_manager">Quality Manager</SelectItem>
                    <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                    <SelectItem value="operations_manager">Operations Manager</SelectItem>
                    <SelectItem value="department_head">Department Head</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStageDialog(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleAddStage} disabled={isSubmitting} className="bg-sky-600 hover:bg-sky-700">
              {isSubmitting ? 'Adding...' : 'Add Stage'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stage Dialog */}
      <Dialog open={showEditStageDialog} onOpenChange={setShowEditStageDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-sky-400" />
              Edit Stage
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modify stage properties and connections
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Stage Name</Label>
              <Input
                value={editStageForm.name}
                onChange={(e) => setEditStageForm({ ...editStageForm, name: e.target.value })}
                placeholder="e.g., Initial Review"
                className="bg-slate-800 border-slate-700 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Stage Type</Label>
                <Select 
                  value={editStageForm.stage_type} 
                  onValueChange={(v) => setEditStageForm({ ...editStageForm, stage_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="approval">Approval</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="decision">Decision</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                    <SelectItem value="timer">Timer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">SLA Hours</Label>
                <Input
                  type="number"
                  value={editStageForm.sla_hours}
                  onChange={(e) => setEditStageForm({ ...editStageForm, sla_hours: parseInt(e.target.value) || 24 })}
                  className="bg-slate-800 border-slate-700 mt-1"
                  min="1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Assignee Type</Label>
                <Select 
                  value={editStageForm.assignee_type} 
                  onValueChange={(v) => setEditStageForm({ ...editStageForm, assignee_type: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="role">Role</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="department">Department</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Assignee</Label>
                <Select 
                  value={editStageForm.assignee_id} 
                  onValueChange={(v) => setEditStageForm({ ...editStageForm, assignee_id: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="safety_manager">Safety Manager</SelectItem>
                    <SelectItem value="quality_manager">Quality Manager</SelectItem>
                    <SelectItem value="compliance_officer">Compliance Officer</SelectItem>
                    <SelectItem value="operations_manager">Operations Manager</SelectItem>
                    <SelectItem value="department_head">Department Head</SelectItem>
                    <SelectItem value="executive">Executive</SelectItem>
                    <SelectItem value="investigator">Investigator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Connections Section */}
            <div className="border-t border-slate-700 pt-4">
              <Label className="text-slate-300 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Connected to (Next Stages)
              </Label>
              <div className="mt-2 space-y-2">
                {editStageForm.next_stage_ids?.length > 0 ? (
                  editStageForm.next_stage_ids.map((nextId) => {
                    const nextStage = selectedTemplate?.stages?.find(s => s.id === nextId);
                    return (
                      <div key={nextId} className="flex items-center justify-between p-2 bg-slate-800 rounded-lg">
                        <span className="text-slate-300 text-sm">{nextStage?.name || nextId}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditStageForm({
                            ...editStageForm,
                            next_stage_ids: editStageForm.next_stage_ids.filter(id => id !== nextId)
                          })}
                          className="h-7 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-slate-500 text-sm italic">No outgoing connections</p>
                )}
              </div>
              
              {/* Add connection dropdown */}
              <div className="mt-3">
                <Label className="text-slate-400 text-xs">Add connection to:</Label>
                <Select 
                  value="" 
                  onValueChange={(v) => {
                    if (v && !editStageForm.next_stage_ids?.includes(v)) {
                      setEditStageForm({
                        ...editStageForm,
                        next_stage_ids: [...(editStageForm.next_stage_ids || []), v]
                      });
                    }
                  }}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                    <SelectValue placeholder="Select a stage..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {selectedTemplate?.stages
                      ?.filter(s => s.id !== editStageForm.id && !editStageForm.next_stage_ids?.includes(s.id))
                      .map(stage => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => handleDeleteStage(editStageForm.id)} 
              disabled={isSubmitting}
              className="border-red-700 text-red-400 hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Stage
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEditStageDialog(false)} className="border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleEditStage} disabled={isSubmitting} className="bg-sky-600 hover:bg-sky-700">
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
